<script setup>
import { computed, onMounted, ref, watch } from 'vue';

// // Store management imports
import { useAwsStore } from '@/stores/aws';
import { useBreakStore } from '@/stores/break';

// // Utils imports
import { logDebug, logError, logWarn } from '@/utils/logger';
import { sendInteractionCompleted } from '@/utils/nacxMetrics';

// // Composables
import { useAssetLoading } from '@/composables/useAssetLoading';
import { useNacxMetrics } from '@/composables/useNacxMetrics';
import { useOverlayNavigation } from '@/composables/useOverlayNavigation';
import { useOverlayTimeline } from '@/composables/useOverlayTimeline';
import { usePollResults } from '@/composables/usePollResults';

// // Module imports
import PollInput from './PollInput.vue';
import PollOutput from './PollOutput.vue';
import { PollSimpleOptions } from './pollSimpleUtils';

// // Asset loading
const { isAssetsLoaded, isTemplateReadyToFadeIn, waitForComponentAssets, templateFadeInHandler } =
  useAssetLoading(5000);
const containerRef = ref();

// // Timeline
const spinnerDurationMS = 1500;
const introDurationMS = 3000;
const dismissDurationMS = 500;

const {
  currentScreen: pollState,
  isDismissing,
  isGone,
  dismiss,
  skipToScreen,
  getScreenDuration,
} = useOverlayTimeline({
  screens: [
    { name: 'spinner', duration: spinnerDurationMS },
    { name: 'intro', duration: introDurationMS },
    { name: 'question', minDuration: 13000, maxDuration: 15000 },
    { name: 'result', minDuration: 13000, maxDuration: 15000 },
    { name: 'dismiss', duration: dismissDurationMS },
  ],
  maxOnScreenDuration: 35000,
  fallbackSlotDuration: 60000,
});

const storeAws = useAwsStore();
const storeBreak = useBreakStore();

// markDismissed is for user-initiated dismiss only — there's no auto-fired
// "closed" event; when the overlay actually closes is up to the TV parent.
// Arrow/Enter are fully consumed by useOverlayNavigation below, so we don't
// forward them to TV Plus (still tracked as NACX key_press metrics though).
const { markDismissed, trackKeyPress } = useNacxMetrics({
  suppressForwardingKeys: ['ArrowUp', 'ArrowDown', 'Enter'],
});

const hasDismissalButton = ref(true);
const hasProgressBar = ref(true);

// // To store the AWS credential provider (reactive)
const awsCredentialProvider = ref(null);

const { calculatedPollPercentages, getSelectionHandler, castVote } = usePollResults({
  optionKeys: Object.values(PollSimpleOptions),
  getBreakUuid: () => storeBreak.breakUuid,
  getAwsCredentialProvider: () => awsCredentialProvider.value,
  // This overlay already initializes NACX metrics; the other 3 poll
  // templates report via a beacon ping instead (usePollResults' default).
  errorReporting: 'nacx',
});

const getPollColors = computed(() => {
  return {
    bar: 'rgba(99, 99, 104, 1)',
    resultsBox: 'rgba(0, 0, 0, 0.7)',
  };
});

// // TempPollStreamOverlay handler functions

const templateFadeOutHandler = () => {
  dismiss();
};

const { activeIndex, isDismissInFocus } = useOverlayNavigation({
  trackKeyPress,
  currentScreen: pollState,
  getOptionCount: () => storeBreak.breakOptions.length,
  hasDismissalButton,
  onSelect: () => buttonSelectHandler(),
  onDismiss: () => {
    markDismissed();
    templateFadeOutHandler();
  },
});

const buttonSelectHandler = async () => {
  // hasNavigationFrozen is already set by useOverlayNavigation before onSelect fires.
  // castVote handles the get-then-put fallback, the optimistic result, and
  // scheduling the graceful update + bounded retry — see usePollResults.
  await castVote(activeIndex.value, storeBreak.breakOptions[activeIndex.value].optionLabel);

  // // NACX Metrics
  sendInteractionCompleted({
    interaction: {
      type: 'question_answered',
      entity: [{ type: 'option_selected', value: String(activeIndex.value + 1) }],
    },
  });

  skipToScreen('result');
};

// // TempPollStreamOverlay lifecycle hooks

onMounted(async () => {
  // // Wait for all assets to load before showing content
  await waitForComponentAssets(containerRef.value);
  templateFadeInHandler();

  try {
    const identityPoolId = import.meta.env.VITE_APP_IDENTITY_POOL_ID;
    if (identityPoolId) {
      if (storeAws.isInitialized.value && storeAws.awsCredentialProvider.value) {
        awsCredentialProvider.value = storeAws.awsCredentialProvider.value;
      } else {
        awsCredentialProvider.value = await storeAws.initializeAwsCredentials(
          identityPoolId,
          import.meta.env.VITE_APP_AWS_REGION || 'us-east-1',
        );
        logDebug('AWS Cognito initialized successfully in TempPollStreamOverlay.');
      }
    } else {
      logWarn('VITE_APP_IDENTITY_POOL_ID is not set.');
    }
  } catch (error) {
    logError('Error during onMounted in TempPollStreamOverlay:', error);
  }
});

// // TempPollStreamOverlay watchers

// // Watch for isBreakReady to become true before making API calls
// // This prevents the race condition where breakUuid is not yet available
watch(
  [() => storeBreak.isBreakReady, awsCredentialProvider],
  ([isBreakReadyNewVal, credentialProvider]) => {
    if (isBreakReadyNewVal && credentialProvider) {
      logDebug('isBreakReady is true and credentials are ready, fetching poll data');
      // // No vote yet; do a background call to get raw starting results
      getSelectionHandler('isBreakReadyWatcher');
    }
  },
);
</script>

<template>
  <!-- v-show="isAssetsLoaded"
  ref="containerRef" -->
  <section
    class="poll-stream-overlay"
    :class="{
      'is-clear': !isTemplateReadyToFadeIn || !isAssetsLoaded,
      'is-opaque': isTemplateReadyToFadeIn && isAssetsLoaded,
      'poll-stream-overlay--vanish': isDismissing,
      'is-gone': isGone,
    }"
  >
    <div class="poll-stream-overlay__container">
      <div v-if="pollState === 'spinner'" class="poll-stream-overlay__spinner"></div>

      <div
        class="poll-stream-overlay__intro"
        :class="{
          'poll-stream-overlay__intro--closed': pollState !== 'intro',
          'poll-stream-overlay__intro--open': pollState === 'intro',
        }"
      >
        <span class="poll-stream-overlay__intro-copy">Grab your remote</span>
      </div>

      <!-- v-show="pollState === 'question'" -->
      <PollInput
        :class="{
          'is-clear': pollState !== 'question',
          'is-opaque': pollState === 'question',
        }"
        :has-poll-header="true"
        :has-dismissal="hasDismissalButton"
        :has-progress="hasProgressBar"
        :has-progress-begun="pollState === 'question'"
        :has-orbs="true"
        :is-focus-on-dismiss="isDismissInFocus"
        :ad-vote-state="storeBreak.breakTaglineCopy"
        :poll-options="storeBreak.breakOptions"
        :focus-index="activeIndex"
        :break-uuid="storeBreak.breakUuid"
        :progress-duration="getScreenDuration('question')"
      />

      <!-- v-show="pollState === 'result'" -->
      <PollOutput
        :class="{
          'is-clear': pollState !== 'result' && pollState !== 'dismiss',
          'is-opaque': pollState === 'result' || pollState === 'dismiss',
        }"
        :has-poll-header="true"
        :has-dismissal="hasDismissalButton"
        :has-progress="hasProgressBar"
        :has-progress-begun="pollState === 'result'"
        :has-orbs="true"
        :is-excluding-check="true"
        :is-focus-on-dismiss="isDismissInFocus"
        :ad-vote-state="storeBreak.breakTaglineCopy"
        :poll-options="storeBreak.breakOptions"
        :focus-index="activeIndex"
        :break-uuid="storeBreak.breakUuid"
        :poll-percentages="calculatedPollPercentages"
        :bar-color="getPollColors.bar"
        :results-box-background-color="getPollColors.resultsBox"
        :progress-duration="getScreenDuration('result')"
      />
    </div>
  </section>
</template>
